import { useState, useEffect } from 'react';

export interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  fingerprinting: boolean;
}

interface StoredConsent {
  version: number;
  timestamp: number;
  preferences: CookiePreferences;
}

const STORAGE_KEY = 'cookie-preferences';
const STORAGE_VERSION = 1;
const CONSENT_DURATION = 365 * 24 * 60 * 60 * 1000; // 1 year in ms

const isConsentValid = (stored: StoredConsent): boolean => {
  return stored.version === STORAGE_VERSION && 
         (Date.now() - stored.timestamp) < CONSENT_DURATION;
};

export const useCookieConsent = () => {
  const [preferences, setPreferences] = useState<CookiePreferences | null>(null);
  const [hasConsented, setHasConsented] = useState(false);

  useEffect(() => {
    const load = () => {
      try {
        const stored =
          localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed: StoredConsent = JSON.parse(stored);
          if (isConsentValid(parsed)) {
            setPreferences(parsed.preferences);
            setHasConsented(true);
            return;
          }
          localStorage.removeItem(STORAGE_KEY);
          sessionStorage.removeItem(STORAGE_KEY);
        }
        setPreferences(null);
        setHasConsented(false);
      } catch (error) {
        console.error('Error reading cookie preferences:', error);
        localStorage.removeItem(STORAGE_KEY);
      }
    };

    load();
    window.addEventListener('cookieConsentChanged', load);
    window.addEventListener('storage', load);
    return () => {
      window.removeEventListener('cookieConsentChanged', load);
      window.removeEventListener('storage', load);
    };
  }, []);

  const broadcast = () => {
    window.dispatchEvent(new Event('cookieConsentChanged'));
  };

  const savePreferences = (prefs: CookiePreferences) => {
    try {
      const storedConsent: StoredConsent = {
        version: STORAGE_VERSION,
        timestamp: Date.now(),
        preferences: prefs,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(storedConsent));
      setPreferences(prefs);
      setHasConsented(true);
      broadcast();
    } catch (error) {
      console.error('Error saving cookie preferences:', error);
      try {
        const storedConsent: StoredConsent = {
          version: STORAGE_VERSION,
          timestamp: Date.now(),
          preferences: prefs,
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storedConsent));
        setPreferences(prefs);
        setHasConsented(true);
        broadcast();
      } catch (sessionError) {
        console.error('Error saving to sessionStorage:', sessionError);
      }
    }
  };

  const resetPreferences = () => {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    setPreferences(null);
    setHasConsented(false);
    broadcast();
  };

  const canUseAnalytics = () => preferences?.analytics ?? false;
  const canUseMarketing = () => preferences?.marketing ?? false;
  const canUseFunctional = () => preferences?.functional ?? false;
  const canUseFingerprinting = () => preferences?.fingerprinting ?? false;

  return {
    preferences,
    hasConsented,
    savePreferences,
    resetPreferences,
    canUseAnalytics,
    canUseMarketing,
    canUseFunctional,
    canUseFingerprinting,
  };
};
